import React, { useState, useEffect, useCallback } from 'react';
import BBALoader from '../components/BBALoader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { FaFileAlt, FaPalette, FaDollarSign, FaPlug } from 'react-icons/fa';
import BackButton from '../components/BackButton';
import { useTechnologyEmbedded } from '../hooks/useTechnologyEmbedded';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanyId } from '../hooks/useCompanyId';
import {
  saveUserPluginStatus,
  getUserPluginData,
  saveUserPluginCustomizations
} from '../services/userPluginService';
import {
  getDefaultEstimateCalculatorConfig,
  deepMergeEstimateConfig,
  syncRoutingWithQuestion1
} from '../data/estimateCalculatorDefaults';
import PortalBackground from '../components/PortalBackground';

const ESTIMATE_API_SETUP_VIDEO =
  'https://www.youtube.com/watch?v=Lj43aSwNpog&t=139s';

const MAIN_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'integration', label: 'Integration settings' },
  { id: 'estimate', label: 'Estimate & completion' },
  { id: 'form', label: 'Form content' },
  { id: 'finalize', label: 'Finalize' }
];

const OPERATOR_CHOICES = ['×', '+', '−', '÷'];

/** Max scenario combinations computed per project type on the Finalize tab. */
const SCENARIO_LIMIT = 2000;

function applyOperator(total, op, value) {
  const v = Number(value) || 0;
  switch (op) {
    case '+':
      return total + v;
    case '−':
      return total - v;
    case '÷':
      return v === 0 ? total : total / v;
    case '×':
    default:
      return total * v;
  }
}

/** Base cost run through the Q3→Q6 multipliers in order, using this route's operators. */
function computeScenarioTotal(baseCost, operators, values) {
  let total = Number(baseCost) || 0;
  values.forEach((v, i) => {
    total = applyOperator(total, operators?.[i] || OPERATOR_CHOICES[i], v);
  });
  return total;
}

function formatCurrency(n) {
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}

/** Every Q3–Q6 answer combination for one route, with its resulting estimate. */
function buildScenarioRows(bundle, baseCost, operators, limit = SCENARIO_LIMIT) {
  const questionOptions = (bundle?.questions || []).map((q) => q.options || []);
  if (questionOptions.length === 0 || questionOptions.some((o) => o.length === 0)) {
    return { rows: [], totalCombos: 0, truncated: false, min: null, max: null };
  }
  const totalCombos = questionOptions.reduce((acc, o) => acc * o.length, 1);
  const cap = Math.min(totalCombos, limit);
  const rows = [];
  let min = Infinity;
  let max = -Infinity;

  for (let n = 0; n < cap; n++) {
    let rem = n;
    const chosen = new Array(questionOptions.length);
    for (let i = questionOptions.length - 1; i >= 0; i--) {
      const opts = questionOptions[i];
      chosen[i] = opts[rem % opts.length];
      rem = Math.floor(rem / opts.length);
    }
    const total = computeScenarioTotal(
      baseCost,
      operators,
      chosen.map((c) => c.value)
    );
    if (total < min) min = total;
    if (total > max) max = total;
    rows.push({ id: n, labels: chosen.map((c) => c.label), total });
  }

  return {
    rows,
    totalCombos,
    truncated: totalCombos > cap,
    min: rows.length ? min : null,
    max: rows.length ? max : null
  };
}

const FORM_SUBTABS = [
  { id: 'q1', label: 'Question 1' },
  { id: 'q2', label: 'Question 2' },
  { id: 'q7', label: 'Question 7' },
  { id: 'q9', label: 'Question 9' },
  { id: 'routing', label: 'Questions 3–6 routing' },
  { id: 'q8', label: 'Question 8 (photos)' },
  { id: 'q10', label: 'Question 10 (unlock)' }
];

function inputClass(darkMode) {
  return `w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  } focus:ring-2 focus:ring-red-500 focus:border-red-500`;
}

function labelClass(darkMode) {
  return `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`;
}

function SectionShell({ title, children, darkMode }) {
  return (
    <div
      className={`rounded-xl border p-4 mb-4 ${
        darkMode ? 'bg-gray-800/80 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}
    >
      <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

const LeadCalculatorPage = ({ embedded: embeddedProp, overrideUserId = null, overrideCompanyId = null }) => {
  const { darkMode } = useTheme();
  const embedded = useTechnologyEmbedded(embeddedProp);
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  // In the CRM Portal View this page is scoped to the client's user/company;
  // otherwise it reads/writes the signed-in user's own plugin data.
  const targetUid = overrideUserId || currentUser?.uid;
  const targetCompanyId = overrideCompanyId || companyId;
  const [calculators, setCalculators] = useState([]);
  const [userPluginData, setUserPluginData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [configurationSavedOnce, setConfigurationSavedOnce] = useState(false);

  const [pageTab, setPageTab] = useState('overview');
  const [formSubTab, setFormSubTab] = useState('q1');
  const [routingRouteKey, setRoutingRouteKey] = useState('kitchenRenovation');
  const [finalizeRouteKey, setFinalizeRouteKey] = useState('kitchenRenovation');
  const [scenarioPicks, setScenarioPicks] = useState({});
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  const [estimateConfig, setEstimateConfig] = useState(() =>
    getDefaultEstimateCalculatorConfig()
  );
  const [configSaving, setConfigSaving] = useState(false);
  const [configSavedFlash, setConfigSavedFlash] = useState(false);

  const pluginDocId = calculators[0]?.docId;

  useEffect(() => {
    loadCalculators();
  }, []);

  useEffect(() => {
    if (targetUid) loadUserPluginData();
  }, [targetUid]);

  useEffect(() => {
    if (targetUid && calculators.length > 0) loadUserPluginData();
  }, [calculators, targetUid]);

  useEffect(() => {
    if (!pluginDocId || !userPluginData) return;
    const defaults = getDefaultEstimateCalculatorConfig();
    const saved = userPluginData[pluginDocId]?.customizations?.estimateCalculator;
    let merged = deepMergeEstimateConfig(defaults, saved);
    const rk = userPluginData[pluginDocId]?.requestApiKey;
    if (rk && !(merged.settings && merged.settings.apiKey)) {
      merged = {
        ...merged,
        settings: {
          ...merged.settings,
          apiKey: rk
        }
      };
    }
    setEstimateConfig(syncRoutingWithQuestion1(merged));
    if (saved && typeof saved === 'object') {
      setConfigurationSavedOnce(true);
    }
  }, [pluginDocId, userPluginData]);

  const loadCalculators = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'pluginTemplates'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const calculatorsData = querySnapshot.docs
        .map((docSnap, index) => {
          const data = docSnap.data();
          return {
            id: `calculator${index + 1}`,
            docId: docSnap.id,
            name: data.title || 'Untitled Calculator',
            basePrice: data.basePrice || 50000,
            rangePercentage: data.rangePercentage || 30,
            sections: data.sections || [],
            isActive: data.isActive ?? true,
            description: data.description || '',
            exampleLink: data.exampleLink || '',
            features: data.features || [],
            ...data
          };
        })
        .filter((calc) => calc.name === 'Estimate & Rendering Calculator');
      setCalculators(calculatorsData);
    } catch (error) {
      console.error('Error loading calculators:', error);
      setCalculators([
        {
          id: 'estimate-rendering',
          docId: null,
          name: 'Estimate & Rendering Calculator',
          description: 'Offline preview — connect Firestore for live plugin template.',
          isActive: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPluginData = async () => {
    try {
      const data = await getUserPluginData(targetUid, targetCompanyId || null);
      setUserPluginData(data);
    } catch (error) {
      console.error('Error loading user plugin data:', error);
    }
  };

  const getUserPluginStatus = (pluginId) => {
    if (!currentUser || !userPluginData[pluginId]) return 'off';
    return userPluginData[pluginId].status || 'off';
  };

  const handlePluginStatusToggle = async (pluginId, event) => {
    event.stopPropagation();
    if (!currentUser) return;
    try {
      const currentStatus = getUserPluginStatus(pluginId);
      let newStatus;
      switch (currentStatus) {
        case 'off':
          newStatus = 'requested';
          break;
        case 'requested':
          newStatus = 'off';
          break;
        case 'on':
          return;
        default:
          newStatus = 'requested';
      }
      setUserPluginData((prev) => ({
        ...prev,
        [pluginId]: {
          ...prev[pluginId],
          status: newStatus,
          updatedAt: new Date().toISOString()
        }
      }));
      await saveUserPluginStatus(targetUid, pluginId, newStatus, targetCompanyId || null, null);
    } catch (error) {
      console.error('Error updating plugin status:', error);
      alert('Error updating plugin status. Please try again.');
    }
  };

  const handleSubmitPluginRequest = async () => {
    if (!currentUser || !pluginDocId) return;
    const trimmed = (estimateConfig.settings?.apiKey || '').trim();
    if (!configurationSavedOnce) {
      alert(
        'Save your configuration first using Save configuration (complete Estimate & completion and Form content at minimum), then request integration.'
      );
      return;
    }
    if (!trimmed) {
      alert('Enter your API key under Integration settings before requesting.');
      setPageTab('integration');
      return;
    }
    setSubmittingRequest(true);
    try {
      await saveUserPluginStatus(
        targetUid,
        pluginDocId,
        'requested',
        targetCompanyId || null,
        { apiKey: trimmed }
      );
      setUserPluginData((prev) => ({
        ...prev,
        [pluginDocId]: {
          ...prev[pluginDocId],
          status: 'requested',
          requestApiKey: trimmed,
          updatedAt: new Date().toISOString()
        }
      }));
    } catch (error) {
      console.error('Error submitting plugin request:', error);
      alert('Could not submit your request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleSaveEstimateConfig = useCallback(async () => {
    if (!currentUser || !pluginDocId) {
      alert('Calculator template not loaded.');
      return;
    }
    if (!(estimateConfig.settings?.apiKey || '').trim()) {
      alert('API key is required. Enter it under Integration settings.');
      setPageTab('integration');
      return;
    }
    setConfigSaving(true);
    try {
      await saveUserPluginCustomizations(
        targetUid,
        pluginDocId,
        { estimateCalculator: estimateConfig },
        targetCompanyId || null
      );
      setConfigSavedFlash(true);
      setTimeout(() => setConfigSavedFlash(false), 2500);
      setConfigurationSavedOnce(true);
      await loadUserPluginData();
    } catch (e) {
      console.error(e);
      alert('Could not save configuration.');
    } finally {
      setConfigSaving(false);
    }
  }, [currentUser, targetUid, pluginDocId, estimateConfig, targetCompanyId]);

  const resetSlice = (sliceKey) => {
    const fresh = getDefaultEstimateCalculatorConfig();
    setEstimateConfig((prev) =>
      syncRoutingWithQuestion1({
        ...prev,
        [sliceKey]: JSON.parse(JSON.stringify(fresh[sliceKey]))
      })
    );
  };

  /** Question 1 owns the Q3–Q6 routes, so every option edit re-syncs routing. */
  const updateQuestion1Options = (makeOptions) => {
    setEstimateConfig((p) =>
      syncRoutingWithQuestion1({
        ...p,
        question1: { ...p.question1, options: makeOptions(p.question1.options) }
      })
    );
  };

  const calculator = calculators[0];
  const userStatus = calculator?.docId ? getUserPluginStatus(calculator.docId) : 'off';

  const tabBarClass = (active) =>
    `flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
      active
        ? darkMode
          ? 'bg-gray-700 text-white'
          : 'bg-white text-gray-900 shadow'
        : darkMode
          ? 'text-gray-400 hover:text-white'
          : 'text-gray-600 hover:text-gray-900'
    }`;

  // Route order/labels follow Question 1's options — that's the source of truth.
  const routeKeys = (estimateConfig.question1?.options || [])
    .map((o) => o.routeKey)
    .filter((k) => k && estimateConfig.routing?.[k]);
  const activeRouteKey = routeKeys.includes(routingRouteKey)
    ? routingRouteKey
    : routeKeys[0];
  const routingBundle = activeRouteKey ? estimateConfig.routing[activeRouteKey] : null;

  // --- Finalize tab: base cost + Q3–Q6 multipliers rolled up per project type ---
  const finalizeKey = routeKeys.includes(finalizeRouteKey)
    ? finalizeRouteKey
    : routeKeys[0];
  const finalizeOption = (estimateConfig.question1?.options || []).find(
    (o) => o.routeKey === finalizeKey
  );
  const finalizeBundle = finalizeKey ? estimateConfig.routing[finalizeKey] : null;
  const finalizeOperators =
    estimateConfig.estimateFormulas?.[finalizeKey]?.operators || OPERATOR_CHOICES;
  const finalizeQuestions = finalizeBundle?.questions || [];
  const picks = finalizeQuestions.map((q, qi) => {
    const saved = scenarioPicks[finalizeKey]?.[qi] ?? 0;
    return Math.min(saved, Math.max((q.options || []).length - 1, 0));
  });
  const pickedOptions = finalizeQuestions.map((q, qi) => (q.options || [])[picks[qi]]);
  // Running total after each question, so the formula is visible step by step.
  const runningTotals = [];
  pickedOptions.reduce((total, o, i) => {
    const next = applyOperator(
      total,
      finalizeOperators[i] || OPERATOR_CHOICES[i],
      o?.value ?? 1
    );
    runningTotals.push(next);
    return next;
  }, Number(finalizeOption?.baseCost) || 0);
  const scenarioTotal = runningTotals.length
    ? runningTotals[runningTotals.length - 1]
    : Number(finalizeOption?.baseCost) || 0;
  const rangePercent = Number(estimateConfig.estimateRangePercent) || 0;
  const scenarioLow = scenarioTotal * (1 - rangePercent / 100);
  const scenarioHigh = scenarioTotal * (1 + rangePercent / 100);
  const scenarios =
    pageTab === 'finalize' && finalizeBundle
      ? buildScenarioRows(finalizeBundle, finalizeOption?.baseCost, finalizeOperators)
      : { rows: [], totalCombos: 0, truncated: false, min: null, max: null };

  const setScenarioPick = (qi, oi) =>
    setScenarioPicks((prev) => {
      const current = finalizeQuestions.map((q, i) => prev[finalizeKey]?.[i] ?? 0);
      current[qi] = oi;
      return { ...prev, [finalizeKey]: current };
    });

  const setFinalizeOperator = (i, char) =>
    setEstimateConfig((p) => {
      const existing = p.estimateFormulas?.[finalizeKey] || {};
      const ops = Array.isArray(existing.operators)
        ? [...existing.operators]
        : [...OPERATOR_CHOICES];
      ops[i] = char;
      return {
        ...p,
        estimateFormulas: {
          ...p.estimateFormulas,
          [finalizeKey]: { ...existing, operators: ops }
        }
      };
    });

  const setFinalizeMultiplier = (qi, oi, value) =>
    setEstimateConfig((p) => {
      const bundle = p.routing?.[finalizeKey];
      if (!bundle) return p;
      const questions = JSON.parse(JSON.stringify(bundle.questions));
      if (!questions[qi]?.options?.[oi]) return p;
      questions[qi].options[oi].value = Number(value);
      return {
        ...p,
        routing: { ...p.routing, [finalizeKey]: { ...bundle, questions } }
      };
    });

  const integrationReady =
    configurationSavedOnce && Boolean((estimateConfig.settings?.apiKey || '').trim());

  return (
    <div className={embedded ? '' : 'relative min-h-screen'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && (
        <header>
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center flex-nowrap">
              <div className="flex-1 min-w-0">
                <BackButton onClick={() => { window.location.href = '/portal/marketing'; }} />
              </div>
              <h1
                className={`text-3xl font-bold whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'} flex-1 text-center`}
              >
                Website Calculators
              </h1>
              <div className="flex-1 flex justify-end min-w-0" />
            </div>
          </div>
        </header>
      )}

      <main className={`max-w-7xl mx-auto py-4 ${embedded ? '' : 'px-4 sm:px-6 lg:px-8'} pb-16`}>
        {loading ? (
          <div className="text-center py-12">
            <BBALoader size={48} className="mx-auto" />
            <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Loading calculators...
            </p>
          </div>
        ) : !calculator ? (
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
            No calculator template found.
          </p>
        ) : (
          <div
            className={`rounded-2xl shadow-md overflow-hidden border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">{calculator.name}</h2>
              {(() => {
                switch (userStatus) {
                  case 'active':
                  case 'on':
                    return (
                      <button
                        disabled
                        className="py-2 px-4 rounded-lg font-medium bg-green-500 text-white cursor-not-allowed opacity-75"
                      >
                        Active
                      </button>
                    );
                  case 'in-progress':
                    return (
                      <button
                        disabled
                        className="py-2 px-4 rounded-lg font-medium bg-purple-500 text-white cursor-not-allowed opacity-75"
                      >
                        In Progress
                      </button>
                    );
                  case 'requested':
                    return (
                      <div className="flex justify-center gap-3 flex-wrap">
                        <button
                          disabled
                          className="py-2 px-4 rounded-lg font-medium bg-yellow-500 text-white cursor-not-allowed opacity-75"
                        >
                          Requested
                        </button>
                        <button
                          onClick={(e) => handlePluginStatusToggle(calculator.docId, e)}
                          className="py-2 px-4 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white"
                        >
                          Cancel Request
                        </button>
                      </div>
                    );
                  default:
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          if (!calculator.docId) {
                            alert('Calculator template unavailable.');
                            return;
                          }
                          handleSubmitPluginRequest();
                        }}
                        disabled={submittingRequest || !integrationReady}
                        title={
                          integrationReady
                            ? 'Submit integration request'
                            : 'Save your configuration and add your API key under Integration settings first'
                        }
                        className="py-2 px-4 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submittingRequest ? 'Submitting…' : 'Request integration'}
                      </button>
                    );
                }
              })()}
            </div>

            <div className="p-4 sm:p-6">
              {/* Main tabs + save (editor tabs only) */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
                <div
                  className={`flex flex-wrap rounded-xl p-1 gap-1 min-w-0 flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm`}
                >
                  {MAIN_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setPageTab(t.id)}
                      className={tabBarClass(pageTab === t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                {pageTab !== 'overview' && pluginDocId && (
                  <div className="flex flex-shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                    <button
                      type="button"
                      onClick={handleSaveEstimateConfig}
                      disabled={configSaving}
                      className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      {configSaving ? 'Saving…' : 'Save configuration'}
                    </button>
                    {configSavedFlash && (
                      <span className="text-sm text-green-600 dark:text-green-400">Saved.</span>
                    )}
                  </div>
                )}
              </div>

              {pageTab === 'overview' && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-blue-900/30 border-blue-600 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'
                      }`}
                    >
                      <FaFileAlt className="w-8 h-8 mb-2" />
                      <span className="text-sm font-medium text-center">Customizable Content</span>
                    </div>
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-purple-900/30 border-purple-600 text-purple-200' : 'bg-purple-50 border-purple-200 text-purple-800'
                      }`}
                    >
                      <FaPalette className="w-8 h-8 mb-2" />
                      <span className="text-sm font-medium text-center">Customizable Styling</span>
                    </div>
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-green-900/30 border-green-600 text-green-200' : 'bg-green-50 border-green-200 text-green-800'
                      }`}
                    >
                      <FaDollarSign className="w-8 h-8 mb-2" />
                      <span className="text-sm font-medium text-center">Customizable Pricing</span>
                    </div>
                    <div
                      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 ${
                        darkMode ? 'bg-amber-900/30 border-amber-600 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
                      }`}
                    >
                      <FaPlug className="w-8 h-8 mb-2" />
                      <span className="text-sm font-medium text-center">Integration Capability</span>
                    </div>
                  </div>
                  {calculator.exampleLink && (
                    <a
                      href={calculator.exampleLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-block mb-6 py-2 px-4 rounded-lg font-medium ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      View Example →
                    </a>
                  )}
                  <div className={`mt-2 pt-6 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Try the Calculator
                      </h4>
                      <a
                        href="https://buildersbreakthrough.com/test-page/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-sm ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                      >
                        Open in new tab →
                      </a>
                    </div>
                    <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                      <iframe
                        src="https://buildersbreakthrough.com/test-page/"
                        title="Estimate & Rendering Calculator"
                        className="w-full min-h-[70vh] border-0"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        allow="clipboard-write"
                      />
                    </div>
                  </div>
                </>
              )}

              {pageTab === 'integration' && pluginDocId && (
                <>
                  <SectionShell title="API key" darkMode={darkMode}>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Enter the API key for your estimate integration here (required). Save your configuration on the
                      other tabs, then use <strong>Request integration</strong> in the header. Keys are copy only and
                      not visible during setup.
                    </p>
                    <label className={labelClass(darkMode)}>
                      API key <span className="text-red-600 dark:text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      autoComplete="off"
                      required
                      aria-required="true"
                      value={estimateConfig.settings.apiKey || ''}
                      onChange={(e) =>
                        setEstimateConfig((p) => ({
                          ...p,
                          settings: { ...p.settings, apiKey: e.target.value }
                        }))
                      }
                      placeholder="Paste your API key"
                      className={`${inputClass(darkMode)} mb-4`}
                    />
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
                      <div
                        className={`flex-1 rounded-lg border p-3 text-center ${
                          darkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Step 1
                        </p>
                        <a
                          href="https://platform.openai.com/login"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2"
                        >
                          Go to API Platform →
                        </a>
                      </div>
                      <div className={`text-center text-lg ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>→</div>
                      <div
                        className={`flex-1 rounded-lg border p-3 text-center ${
                          darkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Step 2
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          Create and copy a new key
                        </p>
                      </div>
                      <div className={`text-center text-lg ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>→</div>
                      <div
                        className={`flex-1 rounded-lg border p-3 text-center ${
                          darkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Step 3
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          Paste it into the box above
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch gap-2">
                      <a
                        href={ESTIMATE_API_SETUP_VIDEO}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full sm:w-1/2 rounded-lg border p-3 text-center text-sm font-medium ${
                          darkMode
                            ? 'bg-gray-900 border-gray-600 text-gray-200 hover:bg-gray-800'
                            : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100'
                        }`}
                      >
                        Resource: How to get your API key (YouTube) →
                      </a>
                      <p
                        className={`w-full sm:w-1/2 rounded-lg border p-3 text-center text-sm ${
                          darkMode
                            ? 'bg-gray-900 border-gray-600 text-gray-400'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                        }`}
                      >
                        Tip: Set a Key Usage Limit to avoid excessive use. We recommend $25.
                      </p>
                    </div>
                  </SectionShell>
                  <SectionShell title="Webhook" darkMode={darkMode}>
                    <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Used to send calculator data into your Tech Stack. Optional — only use this if you&apos;re{' '}
                      <strong>not</strong> working with BBA for automations.
                    </p>
                    <label className={labelClass(darkMode)}>
                      <input
                        type="checkbox"
                        checked={estimateConfig.settings.webhookNotApplicable}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            settings: { ...p.settings, webhookNotApplicable: e.target.checked }
                          }))
                        }
                        className="mr-2 align-middle"
                      />
                      Webhook URL not applicable (N/A)
                    </label>
                    {!estimateConfig.settings.webhookNotApplicable && (
                      <div className="mt-3">
                        <label className={labelClass(darkMode)}>Webhook URL (optional)</label>
                        <input
                          type="url"
                          value={estimateConfig.settings.webhookUrl || ''}
                          onChange={(e) =>
                            setEstimateConfig((p) => ({
                              ...p,
                              settings: { ...p.settings, webhookUrl: e.target.value }
                            }))
                          }
                          className={inputClass(darkMode)}
                          placeholder="https://..."
                        />
                      </div>
                    )}
                  </SectionShell>
                </>
              )}

              {pageTab === 'estimate' && pluginDocId && (
                <>
                  <SectionShell title="Estimate ± range" darkMode={darkMode}>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Set the plus/minus percentage range for displayed estimates. Example: 15% on $100,000 →
                      $85,000–$115,000.
                    </p>
                    <div className="flex items-center gap-2 max-w-xs">
                      <label className={labelClass(darkMode)}>Estimate ± range (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={estimateConfig.estimateRangePercent}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            estimateRangePercent: Number(e.target.value) || 0
                          }))
                        }
                        className={inputClass(darkMode)}
                      />
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>%</span>
                    </div>
                  </SectionShell>

                  <SectionShell title="Estimate ready page" darkMode={darkMode}>
                    <p className={`text-xs mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Placeholders: {'{'}project_type{'}'}, {'{'}timeline{'}'}, {'{'}location{'}'}
                    </p>
                    {[
                      ['estimateTitle', 'Estimate Title', false],
                      ['estimateSubtitle', 'Estimate Subtitle', true],
                      ['reimaginedSectionTitle', 'Reimagined Section Title', false],
                      ['yourVisionTitle', 'Your Vision Title', false],
                      ['visionSummaryTemplate', 'Vision Summary Template', true],
                      ['estimatedInvestmentTitle', 'Estimated Investment Title', false],
                      ['locationNoteText', 'Note text (outside service area)', true],
                      ['getStartedTitle', 'Get Started Title', false],
                      ['getStartedSubtitle', 'Get Started Subtitle', true],
                      ['callPhoneDigits', 'Call Phone Number (digits only)', false]
                    ].map(([field, lab, multiline]) => (
                      <div key={field} className="mb-3">
                        <label className={labelClass(darkMode)}>{lab}</label>
                        {multiline ? (
                          <textarea
                            rows={2}
                            value={estimateConfig.estimateReadyPage[field] || ''}
                            onChange={(e) =>
                              setEstimateConfig((p) => ({
                                ...p,
                                estimateReadyPage: { ...p.estimateReadyPage, [field]: e.target.value }
                              }))
                            }
                            className={inputClass(darkMode)}
                          />
                        ) : (
                          <input
                            type="text"
                            value={estimateConfig.estimateReadyPage[field] || ''}
                            onChange={(e) =>
                              setEstimateConfig((p) => ({
                                ...p,
                                estimateReadyPage: { ...p.estimateReadyPage, [field]: e.target.value }
                              }))
                            }
                            className={inputClass(darkMode)}
                          />
                        )}
                      </div>
                    ))}
                    <div className="mb-4">
                      <label className={labelClass(darkMode)}>Preliminary Estimate Note</label>
                      <textarea
                        rows={6}
                        value={estimateConfig.estimateReadyPage.preliminaryEstimateNote || ''}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            estimateReadyPage: {
                              ...p.estimateReadyPage,
                              preliminaryEstimateNote: e.target.value
                            }
                          }))
                        }
                        className={inputClass(darkMode)}
                      />
                    </div>
                    <div className="mb-4">
                      <label className={labelClass(darkMode)}>Schedule Consultation Link</label>
                      <input
                        type="url"
                        value={
                          estimateConfig.estimateReadyPage.scheduleConsultationLink ??
                          estimateConfig.estimateReadyPage.scheduleButtonText ??
                          ''
                        }
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            estimateReadyPage: {
                              ...p.estimateReadyPage,
                              scheduleConsultationLink: e.target.value
                            }
                          }))
                        }
                        className={inputClass(darkMode)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="mb-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={estimateConfig.estimateReadyPage.locationNoteEnabled}
                          onChange={(e) =>
                            setEstimateConfig((p) => ({
                              ...p,
                              estimateReadyPage: {
                                ...p.estimateReadyPage,
                                locationNoteEnabled: e.target.checked
                              }
                            }))
                          }
                          className="mt-1 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          <span className="block font-medium mb-1">
                            Show location note when user is outside service area
                          </span>
                          <span className={`block text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            When enabled, if the visitor&apos;s zip or city is not in your &quot;in range&quot; list
                            below, they see your note and <strong>do not receive a completed estimate</strong> — the
                            flow stops for out-of-area leads.
                          </span>
                        </span>
                      </label>
                    </div>
                    <label className={labelClass(darkMode)}>
                      Zip codes / cities within range (one per row)
                    </label>
                    <textarea
                      rows={5}
                      value={estimateConfig.estimateReadyPage.locationInRangeList || ''}
                      onChange={(e) =>
                        setEstimateConfig((p) => ({
                          ...p,
                          estimateReadyPage: {
                            ...p.estimateReadyPage,
                            locationInRangeList: e.target.value
                          }
                        }))
                      }
                      className={inputClass(darkMode)}
                      placeholder={'37203\nNashville'}
                    />
                  </SectionShell>
                </>
              )}

              {pageTab === 'form' && pluginDocId && (
                <>
                  <div className={`flex flex-wrap rounded-xl p-1 mb-4 gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm`}>
                    {FORM_SUBTABS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setFormSubTab(t.id)}
                        className={tabBarClass(formSubTab === t.id)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {formSubTab === 'q1' && (
                    <SectionShell
                      title="Question 1 — Project type"
                      darkMode={darkMode}
                    >
                      <button
                        type="button"
                        onClick={() => resetSlice('question1')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Header Text</label>
                      <input
                        value={estimateConfig.question1.headerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question1: { ...p.question1, headerText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-3`}
                      />
                      <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Options
                      </p>
                      <div className="flex gap-2 mb-2 items-end">
                        <div className="flex-1 min-w-[120px]" />
                        <div className="w-28 shrink-0 text-center">
                          <span
                            className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            Base cost
                          </span>
                        </div>
                        <div className="w-14 shrink-0" aria-hidden />
                      </div>
                      {estimateConfig.question1.options.map((opt, i) => (
                        <div key={opt.id || i} className="flex flex-wrap gap-2 mb-2 items-end">
                          <input
                            value={opt.label}
                            onChange={(e) =>
                              updateQuestion1Options((opts) => {
                                const next = [...opts];
                                next[i] = { ...next[i], label: e.target.value };
                                return next;
                              })
                            }
                            className={`${inputClass(darkMode)} flex-1 min-w-[120px]`}
                          />
                          <div className="w-28 shrink-0">
                            <input
                              type="number"
                              value={opt.baseCost}
                              onChange={(e) =>
                                updateQuestion1Options((opts) => {
                                  const next = [...opts];
                                  next[i] = {
                                    ...next[i],
                                    baseCost: Number(e.target.value) || 0
                                  };
                                  return next;
                                })
                              }
                              className={`${inputClass(darkMode)} w-full`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateQuestion1Options((opts) => opts.filter((_, j) => j !== i))
                            }
                            className="text-red-600 text-sm px-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          updateQuestion1Options((opts) => [
                            ...opts,
                            {
                              id: `opt-${Date.now()}`,
                              label: 'New option',
                              baseCost: 50000
                            }
                          ])
                        }
                        className="mt-2 text-sm font-medium text-red-600"
                      >
                        Add Option
                      </button>
                      <p className={`mt-4 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        Each option here drives its own route under{' '}
                        <strong>Questions 3–6 routing</strong>. Renaming an option renames its
                        route, removing one removes its route, and adding one creates a new route
                        to fill in.
                      </p>
                    </SectionShell>
                  )}

                  {formSubTab === 'q2' && (
                    <SectionShell title="Question 2 — Property type & rejection" darkMode={darkMode}>
                      <button
                        type="button"
                        onClick={() => resetSlice('question2')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Header Text</label>
                      <input
                        value={estimateConfig.question2.headerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question2: { ...p.question2, headerText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-4`}
                      />
                      <p className="font-medium mb-2">Options</p>
                      {estimateConfig.question2.options.map((opt, i) => (
                        <div key={opt.id || i} className="flex flex-wrap gap-2 mb-2 items-center">
                          <input
                            value={opt.label}
                            onChange={(e) => {
                              const next = [...estimateConfig.question2.options];
                              next[i] = { ...next[i], label: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: { ...p.question2, options: next }
                              }));
                            }}
                            className={`${inputClass(darkMode)} flex-1`}
                          />
                          <label className="text-xs whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={!!opt.reject}
                              onChange={(e) => {
                                const next = [...estimateConfig.question2.options];
                                next[i] = { ...next[i], reject: e.target.checked };
                                setEstimateConfig((p) => ({
                                  ...p,
                                  question2: { ...p.question2, options: next }
                                }));
                              }}
                            />{' '}
                            Reject
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const next = estimateConfig.question2.options.filter((_, j) => j !== i);
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: { ...p.question2, options: next }
                              }));
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question2: {
                              ...p.question2,
                              options: [
                                ...p.question2.options,
                                { id: `q2-${Date.now()}`, label: 'Option', reject: false }
                              ]
                            }
                          }))
                        }
                        className="text-sm font-medium text-red-600 mb-6"
                      >
                        Add Option
                      </button>
                      <h4 className="font-semibold mb-2">Rejection screen</h4>
                      {['header', 'subheader', 'referralHeader'].map((k) => (
                        <div key={k} className="mb-2">
                          <label className={labelClass(darkMode)}>{k}</label>
                          <textarea
                            rows={k === 'subheader' ? 3 : 2}
                            value={estimateConfig.question2.rejection[k]}
                            onChange={(e) =>
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: {
                                  ...p.question2,
                                  rejection: {
                                    ...p.question2.rejection,
                                    [k]: e.target.value
                                  }
                                }
                              }))
                            }
                            className={inputClass(darkMode)}
                          />
                        </div>
                      ))}
                      <p className="font-medium mt-4 mb-2">Referrals</p>
                      {estimateConfig.question2.rejection.referrals.map((ref, i) => (
                        <div key={i} className="flex gap-2 mb-2">
                          <input
                            value={ref.label}
                            onChange={(e) => {
                              const next = [...estimateConfig.question2.rejection.referrals];
                              next[i] = { ...next[i], label: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: {
                                  ...p.question2,
                                  rejection: { ...p.question2.rejection, referrals: next }
                                }
                              }));
                            }}
                            className={inputClass(darkMode)}
                            placeholder="Label"
                          />
                          <input
                            value={ref.url}
                            onChange={(e) => {
                              const next = [...estimateConfig.question2.rejection.referrals];
                              next[i] = { ...next[i], url: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: {
                                  ...p.question2,
                                  rejection: { ...p.question2.rejection, referrals: next }
                                }
                              }));
                            }}
                            className={inputClass(darkMode)}
                            placeholder="URL"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = estimateConfig.question2.rejection.referrals.filter(
                                (_, j) => j !== i
                              );
                              setEstimateConfig((p) => ({
                                ...p,
                                question2: {
                                  ...p.question2,
                                  rejection: { ...p.question2.rejection, referrals: next }
                                }
                              }));
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question2: {
                              ...p.question2,
                              rejection: {
                                ...p.question2.rejection,
                                referrals: [
                                  ...p.question2.rejection.referrals,
                                  { label: 'Partner', url: 'https://' }
                                ]
                              }
                            }
                          }))
                        }
                        className="text-sm text-red-600 mb-4"
                      >
                        Add Referral
                      </button>
                      <label className={labelClass(darkMode)}>Contact Us Anyway button link</label>
                      <input
                        value={estimateConfig.question2.rejection.contactUsAnywayLink}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question2: {
                              ...p.question2,
                              rejection: {
                                ...p.question2.rejection,
                                contactUsAnywayLink: e.target.value
                              }
                            }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-3`}
                      />
                      <label className={labelClass(darkMode)}>Get in touch link</label>
                      <input
                        value={estimateConfig.question2.rejection.getInTouchLink}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question2: {
                              ...p.question2,
                              rejection: {
                                ...p.question2.rejection,
                                getInTouchLink: e.target.value
                              }
                            }
                          }))
                        }
                        className={inputClass(darkMode)}
                      />
                    </SectionShell>
                  )}

                  {formSubTab === 'q7' && (
                    <SectionShell title="Question 7 — Timeline" darkMode={darkMode}>
                      <button
                        type="button"
                        onClick={() => resetSlice('question7')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Header Text</label>
                      <input
                        value={estimateConfig.question7.headerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question7: { ...p.question7, headerText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-4`}
                      />
                      {estimateConfig.question7.options.map((opt, i) => (
                        <div key={opt.id || i} className="flex gap-2 mb-2">
                          <input
                            value={opt.label}
                            onChange={(e) => {
                              const next = [...estimateConfig.question7.options];
                              next[i] = { ...next[i], label: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question7: { ...p.question7, options: next }
                              }));
                            }}
                            className={inputClass(darkMode)}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = estimateConfig.question7.options.filter((_, j) => j !== i);
                              setEstimateConfig((p) => ({
                                ...p,
                                question7: { ...p.question7, options: next }
                              }));
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question7: {
                              ...p.question7,
                              options: [
                                ...p.question7.options,
                                { id: `q7-${Date.now()}`, label: 'New option' }
                              ]
                            }
                          }))
                        }
                        className="text-sm text-red-600"
                      >
                        Add Option
                      </button>
                    </SectionShell>
                  )}

                  {formSubTab === 'q9' && (
                    <SectionShell title="Question 9 — Location" darkMode={darkMode}>
                      <button
                        type="button"
                        onClick={() => resetSlice('question9')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Header Text</label>
                      <input
                        value={estimateConfig.question9.headerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question9: { ...p.question9, headerText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-3`}
                      />
                      <label className={labelClass(darkMode)}>Placeholder Text</label>
                      <input
                        value={estimateConfig.question9.placeholderText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question9: { ...p.question9, placeholderText: e.target.value }
                          }))
                        }
                        className={inputClass(darkMode)}
                      />
                    </SectionShell>
                  )}

                  {formSubTab === 'routing' && !routingBundle && (
                    <SectionShell title="Questions 3–6 routing" darkMode={darkMode}>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Add at least one project type under <strong>Question 1</strong> — each
                        option there gets its own Q3–Q6 route here.
                      </p>
                    </SectionShell>
                  )}

                  {formSubTab === 'routing' && routingBundle && (
                    <>
                      <div className="mb-4 flex flex-wrap gap-2">
                        {routeKeys.map((rk) => (
                          <button
                            key={rk}
                            type="button"
                            onClick={() => setRoutingRouteKey(rk)}
                            className={`px-3 py-1.5 rounded-lg text-sm ${
                              activeRouteKey === rk
                                ? 'bg-red-600 text-white'
                                : darkMode
                                  ? 'bg-gray-700 text-gray-200'
                                  : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            {estimateConfig.routing[rk]?.routeLabel || rk}
                          </button>
                        ))}
                      </div>
                      <SectionShell
                        title={`Route for: "${routingBundle.routeLabel}"`}
                        darkMode={darkMode}
                      >
                        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Each route has exactly four questions (Q3–Q6). Edit question copy, option labels, and numeric multipliers.
                          Route names come from Question 1 — rename them there.
                        </p>
                        {routingBundle.questions.map((q, qi) => (
                          <div
                            key={q.num}
                            className={`mb-8 pb-8 border-b last:border-0 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                          >
                            <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Question {q.num}
                            </h4>
                            <label className={labelClass(darkMode)}>Question Text</label>
                            <input
                              value={q.questionText}
                              onChange={(e) => {
                                const next = JSON.parse(JSON.stringify(routingBundle.questions));
                                next[qi].questionText = e.target.value;
                                setEstimateConfig((p) => ({
                                  ...p,
                                  routing: {
                                    ...p.routing,
                                    [activeRouteKey]: { ...routingBundle, questions: next }
                                  }
                                }));
                              }}
                              className={`${inputClass(darkMode)} mb-2`}
                            />
                            <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Options
                            </p>
                            <div className="flex gap-2 mb-2 items-end">
                              <div className="flex-1 min-w-[160px]" />
                              <div className="w-24 shrink-0 text-center">
                                <span
                                  className={`block text-xs font-medium mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                >
                                  Multiplier
                                </span>
                              </div>
                              <div className="w-14 shrink-0" aria-hidden />
                            </div>
                            {q.options.map((opt, oi) => (
                              <div key={oi} className="flex flex-wrap gap-2 mb-2 items-end">
                                <input
                                  value={opt.label}
                                  onChange={(e) => {
                                    const next = JSON.parse(JSON.stringify(routingBundle.questions));
                                    next[qi].options[oi].label = e.target.value;
                                    setEstimateConfig((p) => ({
                                      ...p,
                                      routing: {
                                        ...p.routing,
                                        [activeRouteKey]: {
                                          ...routingBundle,
                                          questions: next
                                        }
                                      }
                                    }));
                                  }}
                                  className={`${inputClass(darkMode)} flex-1 min-w-[160px]`}
                                />
                                <div className="w-24 shrink-0">
                                  <input
                                    type="number"
                                    step="any"
                                    value={opt.value}
                                    onChange={(e) => {
                                      const next = JSON.parse(JSON.stringify(routingBundle.questions));
                                      next[qi].options[oi].value = Number(e.target.value);
                                      setEstimateConfig((p) => ({
                                        ...p,
                                        routing: {
                                          ...p.routing,
                                          [activeRouteKey]: {
                                            ...routingBundle,
                                            questions: next
                                          }
                                        }
                                      }));
                                    }}
                                    className={`${inputClass(darkMode)} w-full`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = JSON.parse(JSON.stringify(routingBundle.questions));
                                    next[qi].options = next[qi].options.filter((_, j) => j !== oi);
                                    setEstimateConfig((p) => ({
                                      ...p,
                                      routing: {
                                        ...p.routing,
                                        [activeRouteKey]: {
                                          ...routingBundle,
                                          questions: next
                                        }
                                      }
                                    }));
                                  }}
                                  className="text-red-600 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const next = JSON.parse(JSON.stringify(routingBundle.questions));
                                next[qi].options.push({ label: 'New option', value: 1 });
                                setEstimateConfig((p) => ({
                                  ...p,
                                  routing: {
                                    ...p.routing,
                                    [activeRouteKey]: { ...routingBundle, questions: next }
                                  }
                                }));
                              }}
                              className="text-sm text-red-600"
                            >
                              Add Option
                            </button>
                          </div>
                        ))}
                      </SectionShell>
                    </>
                  )}

                  {formSubTab === 'q8' && (
                    <SectionShell title="Question 8 — Photo upload" darkMode={darkMode}>
                      <button
                        type="button"
                        onClick={() => resetSlice('question8')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Header Text</label>
                      <input
                        value={estimateConfig.question8.headerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question8: { ...p.question8, headerText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-3`}
                      />
                      <label className={labelClass(darkMode)}>Image Disclaimer</label>
                      <textarea
                        rows={2}
                        value={estimateConfig.question8.imageDisclaimerText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question8: {
                              ...p.question8,
                              imageDisclaimerText: e.target.value
                            }
                          }))
                        }
                        className={inputClass(darkMode)}
                      />
                    </SectionShell>
                  )}

                  {formSubTab === 'q10' && (
                    <SectionShell title="Question 10 — Unlock form" darkMode={darkMode}>
                      <button
                        type="button"
                        onClick={() => resetSlice('question10')}
                        className="mb-4 text-sm text-red-600 hover:underline"
                      >
                        Reset to Default
                      </button>
                      <label className={labelClass(darkMode)}>Title</label>
                      <input
                        value={estimateConfig.question10.title}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question10: { ...p.question10, title: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-3`}
                      />
                      <label className={labelClass(darkMode)}>Button Text</label>
                      <input
                        value={estimateConfig.question10.buttonText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question10: { ...p.question10, buttonText: e.target.value }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-4`}
                      />
                      <p className="font-medium mb-2">Form Fields</p>
                      {estimateConfig.question10.fields.map((f, i) => (
                        <div key={f.id || i} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 items-end">
                          <input
                            value={f.label}
                            onChange={(e) => {
                              const next = [...estimateConfig.question10.fields];
                              next[i] = { ...next[i], label: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question10: { ...p.question10, fields: next }
                              }));
                            }}
                            className={inputClass(darkMode)}
                            placeholder="Field label"
                          />
                          <input
                            value={f.placeholder}
                            onChange={(e) => {
                              const next = [...estimateConfig.question10.fields];
                              next[i] = { ...next[i], placeholder: e.target.value };
                              setEstimateConfig((p) => ({
                                ...p,
                                question10: { ...p.question10, fields: next }
                              }));
                            }}
                            className={inputClass(darkMode)}
                            placeholder="Placeholder"
                          />
                          <div className="flex gap-2 items-center">
                            <label className="text-xs whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={f.required}
                                onChange={(e) => {
                                  const next = [...estimateConfig.question10.fields];
                                  next[i] = { ...next[i], required: e.target.checked };
                                  setEstimateConfig((p) => ({
                                    ...p,
                                    question10: { ...p.question10, fields: next }
                                  }));
                                }}
                              />{' '}
                              Required
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const next = estimateConfig.question10.fields.filter((_, j) => j !== i);
                                setEstimateConfig((p) => ({
                                  ...p,
                                  question10: { ...p.question10, fields: next }
                                }));
                              }}
                              className="text-red-600 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question10: {
                              ...p.question10,
                              fields: [
                                ...p.question10.fields,
                                {
                                  id: `field-${Date.now()}`,
                                  label: 'New Field',
                                  placeholder: '',
                                  required: false
                                }
                              ]
                            }
                          }))
                        }
                        className="text-sm text-red-600 mb-6"
                      >
                        Add Field
                      </button>
                      <label className={`flex items-center gap-2 mb-2 ${darkMode ? 'text-gray-300' : ''}`}>
                        <input
                          type="checkbox"
                          checked={estimateConfig.question10.contentCheckboxEnabled}
                          onChange={(e) =>
                            setEstimateConfig((p) => ({
                              ...p,
                              question10: {
                                ...p.question10,
                                contentCheckboxEnabled: e.target.checked
                              }
                            }))
                          }
                        />
                        Enable content checkbox
                      </label>
                      <textarea
                        rows={2}
                        value={estimateConfig.question10.contentCheckboxText}
                        onChange={(e) =>
                          setEstimateConfig((p) => ({
                            ...p,
                            question10: {
                              ...p.question10,
                              contentCheckboxText: e.target.value
                            }
                          }))
                        }
                        className={`${inputClass(darkMode)} mb-4`}
                      />
                    </SectionShell>
                  )}
                </>
              )}

              {pageTab === 'finalize' && pluginDocId && (
                <>
                  {!finalizeBundle ? (
                    <SectionShell title="Finalize" darkMode={darkMode}>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Add at least one project type under <strong>Form content ▸ Question 1</strong>{' '}
                        to review its numbers here.
                      </p>
                    </SectionShell>
                  ) : (
                    <>
                      <SectionShell title="Review your estimate outcomes" darkMode={darkMode}>
                        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Pick a project type, then walk through the Q3–Q6 answers to see the exact
                          estimate a lead would get. Base cost, operators, and multipliers are all
                          editable here — changes save with <strong>Save configuration</strong> and
                          apply everywhere else too.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {routeKeys.map((rk) => (
                            <button
                              key={rk}
                              type="button"
                              onClick={() => setFinalizeRouteKey(rk)}
                              className={`px-3 py-1.5 rounded-lg text-sm ${
                                finalizeKey === rk
                                  ? 'bg-red-600 text-white'
                                  : darkMode
                                    ? 'bg-gray-700 text-gray-200'
                                    : 'bg-gray-200 text-gray-800'
                              }`}
                            >
                              {estimateConfig.routing[rk]?.routeLabel || rk}
                            </button>
                          ))}
                        </div>
                      </SectionShell>

                      <SectionShell
                        title={`Build a scenario — "${finalizeBundle.routeLabel}"`}
                        darkMode={darkMode}
                      >
                        <div
                          className={`mb-4 pb-4 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}
                        >
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-1 min-w-[200px]">
                              <label className={labelClass(darkMode)}>
                                Base cost (from Question 1)
                              </label>
                              <input
                                type="number"
                                value={finalizeOption?.baseCost ?? 0}
                                onChange={(e) =>
                                  updateQuestion1Options((opts) =>
                                    opts.map((o) =>
                                      o.routeKey === finalizeKey
                                        ? { ...o, baseCost: Number(e.target.value) || 0 }
                                        : o
                                    )
                                  )
                                }
                                className={inputClass(darkMode)}
                              />
                            </div>
                            <div className="w-32 shrink-0 text-right">
                              <span
                                className={`block text-xs font-medium mb-0.5 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}
                              >
                                Running total
                              </span>
                              <p
                                className={`py-2 text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                              >
                                {formatCurrency(Number(finalizeOption?.baseCost) || 0)}
                              </p>
                            </div>
                          </div>
                        </div>
                        {finalizeQuestions.map((q, qi) => {
                          const options = q.options || [];
                          const picked = options[picks[qi]];
                          return (
                            <div
                              key={q.num}
                              className={`mb-4 pb-4 border-b last:border-0 last:pb-0 last:mb-0 ${
                                darkMode ? 'border-gray-600' : 'border-gray-200'
                              }`}
                            >
                              <label className={labelClass(darkMode)}>
                                Q{q.num} — {q.questionText || 'Untitled question'}
                              </label>
                              <div className="flex flex-wrap items-end gap-2">
                                <div className="w-16 shrink-0">
                                  <span
                                    className={`block text-xs font-medium mb-0.5 text-center ${
                                      darkMode ? 'text-gray-400' : 'text-gray-500'
                                    }`}
                                  >
                                    Applied
                                  </span>
                                  <select
                                    value={finalizeOperators[qi] || OPERATOR_CHOICES[qi]}
                                    onChange={(e) => setFinalizeOperator(qi, e.target.value)}
                                    className={`${inputClass(darkMode)} w-full text-center`}
                                  >
                                    {OPERATOR_CHOICES.map((op) => (
                                      <option key={op} value={op}>
                                        {op}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <select
                                  value={picks[qi]}
                                  onChange={(e) => setScenarioPick(qi, Number(e.target.value))}
                                  className={`${inputClass(darkMode)} flex-1 min-w-[180px]`}
                                >
                                  {options.map((o, oi) => (
                                    <option key={oi} value={oi}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                                {picked && (
                                  <div className="w-28 shrink-0">
                                    <span
                                      className={`block text-xs font-medium mb-0.5 text-center ${
                                        darkMode ? 'text-gray-400' : 'text-gray-500'
                                      }`}
                                    >
                                      Multiplier
                                    </span>
                                    <input
                                      type="number"
                                      step="any"
                                      value={picked.value}
                                      onChange={(e) =>
                                        setFinalizeMultiplier(qi, picks[qi], e.target.value)
                                      }
                                      className={`${inputClass(darkMode)} w-full`}
                                    />
                                  </div>
                                )}
                                <div className="w-32 shrink-0 text-right">
                                  <span
                                    className={`block text-xs font-medium mb-0.5 ${
                                      darkMode ? 'text-gray-400' : 'text-gray-500'
                                    }`}
                                  >
                                    Running total
                                  </span>
                                  <p
                                    className={`py-2 text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                                  >
                                    {formatCurrency(runningTotals[qi])}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div
                          className={`mt-4 rounded-lg border p-4 text-center ${
                            darkMode ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-200'
                          }`}
                        >
                          <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Estimate for this scenario
                          </p>
                          <p className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                            {formatCurrency(scenarioTotal)}
                          </p>
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Shown to the lead as {formatCurrency(scenarioLow)} –{' '}
                            {formatCurrency(scenarioHigh)} (± {rangePercent}%)
                          </p>
                        </div>
                      </SectionShell>

                      <SectionShell title="Every outcome" darkMode={darkMode}>
                        {scenarios.rows.length === 0 ? (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Add at least one option to each of Q3–Q6 for this route to see outcomes.
                          </p>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                              {[
                                ['Lowest outcome', formatCurrency(scenarios.min)],
                                ['Highest outcome', formatCurrency(scenarios.max)],
                                ['Possible combinations', String(scenarios.totalCombos)]
                              ].map(([lab, val]) => (
                                <div
                                  key={lab}
                                  className={`rounded-lg border p-3 text-center ${
                                    darkMode ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-200'
                                  }`}
                                >
                                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {lab}
                                  </p>
                                  <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {val}
                                  </p>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowAllScenarios((v) => !v)}
                              className="text-sm font-medium text-red-600 hover:underline"
                            >
                              {showAllScenarios
                                ? 'Hide the full list'
                                : `Show all ${scenarios.rows.length} outcomes`}
                            </button>
                            {scenarios.truncated && (
                              <p className={`text-xs mt-2 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                Showing the first {scenarios.rows.length} of{' '}
                                {scenarios.totalCombos} combinations — the lowest and highest above
                                cover only those.
                              </p>
                            )}
                            {showAllScenarios && (
                              <div className="mt-4 overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                      {finalizeQuestions.map((q) => (
                                        <th key={q.num} className="text-left font-medium py-2 pr-3">
                                          Q{q.num}
                                        </th>
                                      ))}
                                      <th className="text-right font-medium py-2">Estimate</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {scenarios.rows.map((row) => (
                                      <tr
                                        key={row.id}
                                        className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
                                      >
                                        {row.labels.map((l, li) => (
                                          <td
                                            key={li}
                                            className={`py-2 pr-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                          >
                                            {l}
                                          </td>
                                        ))}
                                        <td
                                          className={`py-2 text-right font-medium whitespace-nowrap ${
                                            darkMode ? 'text-white' : 'text-gray-900'
                                          }`}
                                        >
                                          {formatCurrency(row.total)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </SectionShell>
                    </>
                  )}
                </>
              )}

              {!pluginDocId && pageTab !== 'overview' && (
                <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                  Connect to Firestore plugin templates to edit and save configuration.
                </p>
              )}
            </div>
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default LeadCalculatorPage;
